/**
 * Media Permission Detection Service
 * Detects permission states, browser type, PWA mode, and device capabilities
 */

import {
  PERMISSION_STATES,
  BROWSER_TYPES,
  OS_TYPES,
  DEVICE_TYPES,
  BROWSER_PATTERNS,
  OS_PATTERNS,
  isPWA,
  isSecureContext,
} from './permissionConstants'

// Re-export for convenience
export { isPWA, isSecureContext }

/**
 * Detect the current browser
 */
export const detectBrowser = () => {
  const userAgent = navigator.userAgent

  // Check in order: Edge, Chrome, Safari, Firefox, Samsung, Opera
  // Order matters because Chrome UA includes Safari, Edge includes Chrome, etc.
  if (BROWSER_PATTERNS[BROWSER_TYPES.EDGE].test(userAgent)) {
    return BROWSER_TYPES.EDGE
  }
  if (BROWSER_PATTERNS[BROWSER_TYPES.SAMSUNG].test(userAgent)) {
    return BROWSER_TYPES.SAMSUNG
  }
  if (BROWSER_PATTERNS[BROWSER_TYPES.OPERA].test(userAgent)) {
    return BROWSER_TYPES.OPERA
  }
  if (BROWSER_PATTERNS[BROWSER_TYPES.CHROME].test(userAgent)) {
    return BROWSER_TYPES.CHROME
  }
  if (BROWSER_PATTERNS[BROWSER_TYPES.FIREFOX].test(userAgent)) {
    return BROWSER_TYPES.FIREFOX
  }
  if (BROWSER_PATTERNS[BROWSER_TYPES.SAFARI].test(userAgent)) {
    return BROWSER_TYPES.SAFARI
  }

  return BROWSER_TYPES.UNKNOWN
}

/**
 * Detect the operating system
 */
export const detectOS = () => {
  const userAgent = navigator.userAgent

  if (OS_PATTERNS[OS_TYPES.IOS].test(userAgent)) {
    return OS_TYPES.IOS
  }
  if (OS_PATTERNS[OS_TYPES.ANDROID].test(userAgent)) {
    return OS_TYPES.ANDROID
  }
  if (OS_PATTERNS[OS_TYPES.MAC].test(userAgent)) {
    return OS_TYPES.MAC
  }
  if (OS_PATTERNS[OS_TYPES.WINDOWS].test(userAgent)) {
    return OS_TYPES.WINDOWS
  }
  if (OS_PATTERNS[OS_TYPES.LINUX].test(userAgent)) {
    return OS_TYPES.LINUX
  }

  return OS_TYPES.UNKNOWN
}

/**
 * Detect device type (desktop, mobile, tablet)
 */
export const detectDeviceType = () => {
  const userAgent = navigator.userAgent
  const os = detectOS()

  // iOS and Android are mobile/tablet
  if (os === OS_TYPES.IOS || os === OS_TYPES.ANDROID) {
    // iPad detection
    if (/iPad/.test(userAgent) || (os === OS_TYPES.IOS && 'ontouchend' in document && window.screen.width >= 768)) {
      return DEVICE_TYPES.TABLET
    }
    return DEVICE_TYPES.MOBILE
  }

  // Check for touch screen on desktop
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0

  if (isTouchDevice && window.screen.width < 1024) {
    return DEVICE_TYPES.TABLET
  }

  return DEVICE_TYPES.DESKTOP
}

/**
 * Get complete browser environment info
 */
export const getBrowserEnvironment = () => {
  const browser = detectBrowser()
  const os = detectOS()
  const deviceType = detectDeviceType()
  const pwa = isPWA()
  const secure = isSecureContext()

  return {
    browser,
    os,
    deviceType,
    isPWA: pwa,
    isSecure: secure,
    userAgent: navigator.userAgent,
  }
}

/**
 * Check if browser supports Permissions API
 */
export const supportsPermissionsAPI = () => {
  return 'permissions' in navigator && 'query' in navigator.permissions
}

/**
 * Check permission state using Permissions API
 * Note: Safari has limited support for Permissions API
 */
export const checkPermissionState = async (permissionName) => {
  if (!supportsPermissionsAPI()) {
    return PERMISSION_STATES.UNKNOWN
  }

  try {
    const result = await navigator.permissions.query({ name: permissionName })
    return result.state
  } catch (error) {
    // Safari might throw an error for camera/microphone
    console.warn(`Permission query failed for ${permissionName}:`, error)
    return PERMISSION_STATES.UNKNOWN
  }
}

/**
 * Check both camera and microphone permissions
 */
export const checkMediaPermissions = async () => {
  const cameraState = await checkPermissionState('camera')
  const microphoneState = await checkPermissionState('microphone')

  return {
    camera: cameraState,
    microphone: microphoneState,
    timestamp: new Date().toISOString(),
  }
}

/**
 * Check if devices are available
 */
export const checkDevicesAvailable = async () => {
  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      return {
        hasCamera: false,
        hasMicrophone: false,
        error: 'MediaDevices API not supported',
      }
    }

    const devices = await navigator.mediaDevices.enumerateDevices()
    const videoDevices = devices.filter((device) => device.kind === 'videoinput')
    const audioDevices = devices.filter((device) => device.kind === 'audioinput')

    return {
      hasCamera: videoDevices.length > 0,
      hasMicrophone: audioDevices.length > 0,
      cameraCount: videoDevices.length,
      microphoneCount: audioDevices.length,
      devices,
    }
  } catch (error) {
    console.error('Error checking devices:', error)
    return {
      hasCamera: false,
      hasMicrophone: false,
      error: error.message,
    }
  }
}

/**
 * Attempt to detect permission state through getUserMedia
 * This is a fallback for browsers with limited Permissions API support
 */
export const detectPermissionViaGetUserMedia = async (constraints = { audio: true, video: true }) => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraints)
    
    // Got permission, stop all tracks immediately
    stream.getTracks().forEach((track) => track.stop())
    
    return {
      granted: true,
      state: PERMISSION_STATES.GRANTED,
    }
  } catch (error) {
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      return {
        granted: false,
        state: PERMISSION_STATES.DENIED,
        error: error.name,
      }
    }
    
    if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      return {
        granted: false,
        state: PERMISSION_STATES.UNKNOWN,
        error: 'No devices found',
      }
    }

    return {
      granted: false,
      state: PERMISSION_STATES.UNKNOWN,
      error: error.name || error.message,
    }
  }
}

/**
 * Get comprehensive permission status
 * Uses multiple detection methods for reliability
 */
export const getPermissionStatus = async (mediaType = 'both') => {
  const environment = getBrowserEnvironment()
  const devicesInfo = await checkDevicesAvailable()

  let permissionStates = {
    camera: PERMISSION_STATES.UNKNOWN,
    microphone: PERMISSION_STATES.UNKNOWN,
  }

  // Try Permissions API first
  if (supportsPermissionsAPI()) {
    const permissions = await checkMediaPermissions()
    permissionStates = {
      camera: permissions.camera,
      microphone: permissions.microphone,
    }
  }

  // If we still don't know the state, we'll need to prompt the user
  // or they can trigger getUserMedia

  return {
    environment,
    devices: devicesInfo,
    permissions: permissionStates,
    canRequestPermissions:
      environment.isSecure &&
      (devicesInfo.hasCamera || devicesInfo.hasMicrophone),
  }
}

/**
 * Monitor permission changes
 * Returns a function to stop monitoring
 */
export const monitorPermissionChanges = (onPermissionChange) => {
  if (!supportsPermissionsAPI()) {
    console.warn('Permission monitoring not supported in this browser')
    return () => {}
  }

  const permissionQueries = []
  const handlers = []
  
  const setupMonitor = async () => {
    try {
      const cameraPermission = await navigator.permissions.query({ name: 'camera' })
      const microphonePermission = await navigator.permissions.query({ name: 'microphone' })

      const cameraHandler = () => {
        onPermissionChange({
          type: 'camera',
          state: cameraPermission.state,
        })
      }
      
      const microphoneHandler = () => {
        onPermissionChange({
          type: 'microphone',
          state: microphonePermission.state,
        })
      }

      cameraPermission.addEventListener('change', cameraHandler)
      microphonePermission.addEventListener('change', microphoneHandler)

      permissionQueries.push(cameraPermission, microphonePermission)
      handlers.push(
        { query: cameraPermission, handler: cameraHandler },
        { query: microphonePermission, handler: microphoneHandler }
      )
    } catch (error) {
      console.warn('Could not set up permission monitoring:', error)
    }
  }

  setupMonitor()

  // Return cleanup function
  return () => {
    handlers.forEach(({ query, handler }) => {
      try {
        query.removeEventListener('change', handler)
      } catch (error) {
        console.warn('Error removing permission listener:', error)
      }
    })
    permissionQueries.length = 0
    handlers.length = 0
  }
}

/**
 * Check if running in an iframe (might affect permissions)
 */
export const isInIframe = () => {
  try {
    return window.self !== window.top
  } catch (e) {
    return true
  }
}

/**
 * Get detailed diagnostic information
 */
export const getDiagnosticInfo = async () => {
  const environment = getBrowserEnvironment()
  const permissionStatus = await getPermissionStatus()
  const inIframe = isInIframe()

  return {
    ...permissionStatus,
    inIframe,
    supportsPermissionsAPI: supportsPermissionsAPI(),
    timestamp: new Date().toISOString(),
  }
}

